import { useEffect, useMemo, useState, useCallback } from "react";
import { FileCheck2, ExternalLink, Landmark, Trash2, Save, X, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Pill } from "../common";
import { LINK_LABELS, MISSION_TYPE_LABELS } from "../../pages/VisasHub";

export default function TripDocuments({ tripId }) {
  const [docs, setDocs] = useState(null);
  const [countries, setCountries] = useState([]);
  const [check, setCheck] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transitPick, setTransitPick] = useState("");

  useEffect(() => {
    api.get(`/trips/${tripId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    api.get("/visas/countries").then((r) => setCountries(r.data.items)).catch(() => {});
  }, [tripId]);

  // Live official-links preview whenever a destination is chosen
  useEffect(() => {
    if (!docs?.destination_iso2) { setCheck(null); return; }
    api.get(`/visas/check`, { params: {
      passport: docs.passport_iso2 || docs.destination_iso2,
      destination: docs.destination_iso2,
      transit: docs.transit_iso2s?.length ? docs.transit_iso2s.join(",") : undefined,
    } }).then((r) => setCheck(r.data)).catch(() => setCheck(null));
  }, [docs?.destination_iso2, docs?.passport_iso2, JSON.stringify(docs?.transit_iso2s || [])]); // eslint-disable-line react-hooks/exhaustive-deps

  const byIso = useMemo(() => Object.fromEntries(countries.map((c) => [c.iso2, c.name])), [countries]);
  const update = useCallback((patch) => { setDocs((d) => ({ ...d, ...patch })); setDirty(true); }, []);

  if (!docs) return null;

  const toggleItem = (key) => update({ checklist: docs.checklist.map((i) => i.key === key ? { ...i, done: !i.done } : i) });
  const doneCount = docs.checklist.filter((i) => i.done).length;

  const addTransit = (code) => {
    if (code && !(docs.transit_iso2s || []).includes(code) && (docs.transit_iso2s || []).length < 6) {
      update({ transit_iso2s: [...(docs.transit_iso2s || []), code] });
    }
    setTransitPick("");
  };

  const saveMission = (m) => {
    if ((docs.missions || []).some((x) => x.id === m.id)) { toast.info("Contact already saved"); return; }
    if ((docs.missions || []).length >= 20) { toast.error("Contact limit reached"); return; }
    update({ missions: [...(docs.missions || []), { id: m.id, name: m.name, mission_type: m.mission_type, city: m.city, host_country: m.host_country, address: m.address, phone: m.phone, website: m.website }] });
  };

  const persist = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/trips/${tripId}/documents`, docs);
      setDocs(data); setDirty(false);
      toast.success("Travel documents saved");
    } catch { toast.error("Could not save travel documents"); }
    finally { setSaving(false); }
  };

  const dest = check?.destination;
  return (
    <section className="mb-10" data-testid="trip-documents">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading font-900 text-[#0e7490] text-2xl flex items-center gap-2">
          <FileCheck2 className="w-6 h-6" /> Travel documents
          <span className="text-sm font-mono text-muted-foreground font-400">{doneCount}/{docs.checklist.length} ready</span>
        </h2>
        {dirty && (
          <button onClick={persist} disabled={saving} data-testid="trip-docs-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0e7490] text-white text-sm font-600 hover:opacity-90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save checklist"}
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Checklist */}
        <div className="border border-zinc-300 bg-white p-5" data-testid="trip-docs-checklist">
          <div className="flex flex-wrap gap-3 mb-4">
            <label className="block flex-1 min-w-[150px]">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Passport country</span>
              <select value={docs.passport_iso2 || ""} onChange={(e) => update({ passport_iso2: e.target.value || null })}
                data-testid="trip-docs-passport" className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none">
                <option value="">Select…</option>
                {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
              </select>
            </label>
            <label className="block flex-1 min-w-[150px]">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Destination country</span>
              <select value={docs.destination_iso2 || ""} onChange={(e) => update({ destination_iso2: e.target.value || null })}
                data-testid="trip-docs-destination" className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none">
                <option value="">Select…</option>
                {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <div className="mb-4">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Transit countries</span>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {(docs.transit_iso2s || []).map((code) => (
                <span key={code} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 text-xs font-600">
                  {byIso[code] || code}
                  <button onClick={() => update({ transit_iso2s: docs.transit_iso2s.filter((c) => c !== code) })} aria-label={`Remove ${code}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <select value={transitPick} onChange={(e) => addTransit(e.target.value)} data-testid="trip-docs-transit-select"
                className="border border-dashed border-zinc-400 bg-white px-2 py-1 text-xs text-zinc-600 focus:outline-none">
                <option value="">+ Add…</option>
                {countries.filter((c) => !(docs.transit_iso2s || []).includes(c.iso2) && c.iso2 !== docs.destination_iso2)
                  .map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <ul className="space-y-1.5">
            {docs.checklist.map((item) => (
              <li key={item.key}>
                <label className="flex items-start gap-2.5 cursor-pointer group" data-testid={`trip-doc-item-${item.key}`}>
                  <input type="checkbox" checked={item.done} onChange={() => toggleItem(item.key)}
                    className="mt-0.5 w-4 h-4 accent-[#0e7490]" data-testid={`trip-doc-check-${item.key}`} />
                  <span className={`text-sm ${item.done ? "line-through text-muted-foreground" : "text-zinc-800"}`}>{item.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-4">Only a checklist is stored — never passport numbers or document scans. Verify all requirements with official government sources.</p>
        </div>

        {/* Destination official links + embassy contacts */}
        <div className="space-y-4">
          {dest && (
            <div className="border border-zinc-300 bg-white p-5" data-testid="trip-docs-official-links">
              <h3 className="font-heading font-700 text-base mb-1 flex items-center gap-2"><Landmark className="w-4 h-4 text-[#0e7490]" /> Official resources — {dest.name}</h3>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(dest.entry_programs || []).map((p) => <Pill key={p} tone="green">{p}</Pill>)}
              </div>
              <div className="grid gap-1.5">
                {Object.entries(dest.links || {}).map(([key, url]) => (
                  <a key={key} href={url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between border border-zinc-200 px-3 py-2 text-sm hover:border-[#0e7490] transition-colors">
                    <span>{LINK_LABELS[key] || key}</span><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </a>
                ))}
                {Object.keys(dest.links || {}).length === 0 && <p className="text-sm text-muted-foreground">No verified official links for this country yet — check its embassy or Ministry of Foreign Affairs website.</p>}
              </div>
              {(check?.transit_countries || []).map((tc) => Object.keys(tc.links || {}).length > 0 && (
                <div key={tc.iso2} className="mt-3 pt-3 border-t border-zinc-100">
                  <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1.5">Transit — {tc.name}</div>
                  <div className="grid gap-1.5">
                    {Object.entries(tc.links).slice(0, 3).map(([key, url]) => (
                      <a key={key} href={url} target="_blank" rel="noreferrer" className="flex items-center justify-between border border-zinc-200 px-3 py-2 text-sm hover:border-amber-500 transition-colors">
                        <span>{LINK_LABELS[key] || key}</span><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border border-zinc-300 bg-white p-5" data-testid="trip-docs-contacts">
            <h3 className="font-heading font-700 text-base mb-3">Saved embassy &amp; consulate contacts</h3>
            {(docs.missions || []).length > 0 ? (
              <ul className="space-y-2 mb-3">
                {docs.missions.map((m) => (
                  <li key={m.id} className="border border-zinc-200 p-3 text-sm" data-testid={`trip-doc-mission-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-600">{m.name}</div>
                        <div className="text-xs text-muted-foreground">{MISSION_TYPE_LABELS[m.mission_type] || m.mission_type} · {m.city}, {m.host_country}</div>
                        {m.phone && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</div>}
                      </div>
                      <button onClick={() => update({ missions: docs.missions.filter((x) => x.id !== m.id) })}
                        data-testid={`trip-doc-mission-remove-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {m.website && <a href={m.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Official website ↗</a>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-muted-foreground mb-3">No contacts saved yet.</p>}
            {docs.passport_iso2 && check?.destination_missions_in_home_country?.length > 0 && (
              <>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Suggested for this trip</div>
                <div className="flex flex-wrap gap-2">
                  {check.destination_missions_in_home_country.slice(0, 5).map((m) => (
                    <button key={m.id} onClick={() => saveMission(m)} data-testid={`trip-doc-add-mission-${m.id}`}
                      className="px-2.5 py-1.5 border border-zinc-300 bg-white text-xs hover:border-[#0e7490] transition-colors">+ {m.name}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
